import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Container,
  Group,
  Paper,
  Select,
  Stack,
  TextInput,
  Title,
} from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAdminProjectsList } from '../../queries/admin-projects';
import { useCreateProject } from '../../queries/projects';
import { useCompaniesList } from '../../queries/companies';
import { useAppsList } from '../../queries/apps';
import { notifications } from '@mantine/notifications';
import { ApiError } from '../../lib/api-error';
import type { Project } from '../../types/api';
import { useAuthStore } from '../../auth/auth-store';
import { modals } from '@mantine/modals';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'closed', label: 'Closed' },
];

export function AdminProjectsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'active' | 'archived' | 'closed' | ''>('');

  const filters = useMemo(
    () => ({
      q: q || undefined,
      status: status || undefined,
    }),
    [q, status],
  );

  const { data, isLoading, isError, error } = useAdminProjectsList(filters);
  const createM = useCreateProject();
  const me = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const appsQ = useAppsList();
  const appNameByKey = useMemo(
    () => new Map((appsQ.data ?? []).map((a) => [a.key, a.name])),
    [appsQ.data],
  );

  function openCreate() {
    modals.open({
      title: 'New project',
      children: (
        <CreateProjectForm
          onCreated={(p) => {
            modals.closeAll();
            notifications.show({ title: 'Project created', message: p.name, color: 'pine' });
            navigate(`/projects/${p.id}`);
          }}
          onError={(err) =>
            notifications.show({
              title: 'Create failed',
              message: err,
              color: 'red',
            })
          }
          createMutation={createM}
          defaultOwnerId={me?.id ?? ''}
        />
      ),
    });
  }

  return (
    <Container size="xl">
      <Stack gap="md">
        <Group justify="space-between" align="flex-end">
          <Stack gap={0}>
            <Title order={2}>Projects</Title>
            {data && (
              <Badge variant="light" color="pine" size="sm">
                {data.total} total
              </Badge>
            )}
          </Stack>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            New project
          </Button>
        </Group>

        <Paper withBorder p="sm" radius="md">
          <Group gap="sm">
            <TextInput
              placeholder="Search name or company"
              leftSection={<IconSearch size={14} />}
              value={q}
              onChange={(e) => setQ(e.currentTarget.value)}
              w={320}
            />
            <Select
              data={STATUS_OPTIONS}
              value={status}
              onChange={(v) => setStatus((v ?? '') as 'active' | 'archived' | 'closed' | '')}
              w={170}
            />
          </Group>
        </Paper>

        <Paper withBorder radius="md">
          <DataTable<Project>
            records={data?.projects ?? []}
            fetching={isLoading}
            minHeight={200}
            noRecordsText={
              isError ? `Failed to load: ${(error as Error).message}` : 'No projects yet'
            }
            highlightOnHover
            onRowClick={({ record }) => navigate(`/projects/${record.id}`)}
            columns={[
              { accessor: 'name', title: 'Name' },
              { accessor: 'company_name', title: 'Company' },
              {
                accessor: 'app_key',
                title: 'App',
                width: 140,
                render: (r) => (
                  <Badge variant="light" color="gray">
                    {appNameByKey.get(r.app_key) ?? r.app_key}
                  </Badge>
                ),
              },
              {
                accessor: 'status',
                title: 'Status',
                width: 120,
                render: (r) => <Badge variant="light">{r.status}</Badge>,
              },
              {
                accessor: 'updated_at',
                title: 'Updated',
                width: 160,
                render: (r) => dayjs(r.updated_at).format('YYYY-MM-DD HH:mm'),
              },
            ]}
          />
        </Paper>
      </Stack>
    </Container>
  );
}

function CreateProjectForm({
  onCreated,
  onError,
  createMutation,
  defaultOwnerId,
}: {
  onCreated: (p: Project) => void;
  onError: (msg: string) => void;
  createMutation: ReturnType<typeof useCreateProject>;
  defaultOwnerId: string;
}) {
  const [name, setName] = useState('');
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [appKey, setAppKey] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const companiesQ = useCompaniesList({ limit: 200 });
  const appsQ = useAppsList();

  return (
    <Stack gap="sm">
      <TextInput
        label="Name"
        required
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />
      <Select
        label="Company"
        required
        searchable
        placeholder="Pick a company"
        nothingFoundMessage="No matches — create one in Admin → Companies first"
        data={(companiesQ.data?.companies ?? []).map((c) => ({ value: c.id, label: c.name }))}
        value={companyId}
        onChange={(v) => setCompanyId(v)}
      />
      <Select
        label="App"
        required
        placeholder="Pick an app"
        nothingFoundMessage="No apps configured"
        data={(appsQ.data ?? []).map((a) => ({ value: a.key, label: a.name }))}
        value={appKey}
        onChange={(v) => setAppKey(v)}
      />
      <TextInput
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
      />
      <Group justify="flex-end" mt="sm">
        <Button variant="default" onClick={() => modals.closeAll()}>
          Cancel
        </Button>
        <Button
          loading={createMutation.isPending}
          disabled={!name || !companyId || !appKey}
          onClick={async () => {
            if (!name || !companyId || !appKey) return;
            try {
              const payload: {
                name: string;
                company_id: string;
                app_key: string;
                description?: string | null;
                owner_id?: string;
              } = {
                name,
                company_id: companyId,
                app_key: appKey,
                description: description || null,
              };
              if (defaultOwnerId) payload.owner_id = defaultOwnerId;
              const res = await createMutation.mutateAsync(payload);
              onCreated(res.project as Project);
            } catch (err) {
              onError(err instanceof ApiError ? err.message : 'Unexpected error');
            }
          }}
        >
          Create
        </Button>
      </Group>
    </Stack>
  );
}
